import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { m, AnimatePresence } from 'framer-motion'
import FeedbackIcon from '@icons/chat-bubble-left-right'
import CloseIcon from '@icons/x-mark'
import TabList2, { TabElement } from '@/components/TabList2'
import { AppService } from '@/lib/services/AppService'
import GenericInput from './GenericInput'
import Textarea from './Textarea'
import Button from './Button'
import CheckIcon from '@icons/check'
import { FeedbackType } from '@/lib/definitions/api.types'
import type { FeedbackPrefill } from '@/lib/provider/FeedbackProvider'

interface FeedbackPanelProps {
  isOpen: boolean
  onClose: () => void
  prefill?: FeedbackPrefill
}

interface FeedbackFormData {
  type: 'feedback' | 'bug'
  subject: string
  description: string
}

const schema = Yup.object().shape({
  type: Yup.string().oneOf(['feedback', 'bug']).required(),
  subject: Yup.string().required('Subject is required'),
  description: Yup.string().required('Description is required'),
})

const appService = new AppService()

const FeedbackPanel = ({ isOpen, onClose, prefill }: FeedbackPanelProps) => {
  const location = useLocation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submissionSuccess, setSubmissionSuccess] = useState(false)
  const [submittedType, setSubmittedType] = useState<'feedback' | 'bug' | null>(
    null
  )
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FeedbackFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      type: 'feedback',
      subject: '',
      description: '',
    },
  })

  const feedbackType: FeedbackType = watch('type')

  useEffect(() => {
    if (!isOpen || !prefill) return

    if (prefill.type) setValue('type', prefill.type)
    if (prefill.subject)
      setValue('subject', prefill.subject, { shouldValidate: true })
  }, [isOpen, prefill, setValue])

  const feedbackTabs: TabElement[] = [
    {
      id: 'feedback',
      label: 'Feedback',
    },
    {
      id: 'bug',
      label: 'Bug Report',
    },
  ]

  const onSubmit = async (data: FeedbackFormData) => {
    setIsSubmitting(true)
    try {
      await appService.submitFeedback({
        ...data,
        debugInfo: {
          url: window.location.href,
          path: location.pathname,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight,
          },
          referrer: document.referrer,
          language: navigator.language,
          platform: navigator.platform,
        },
      })

      setSubmittedType(data.type)
      reset()
      setSubmissionSuccess(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (isSubmitting) return
    reset()
    setSubmissionSuccess(false)
    setSubmittedType(null)
    onClose()
  }

  const handleTabClick = (tabId: string) => {
    setValue('type', tabId as FeedbackType)
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <m.div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={handleClose}
          />

          {/* Feedback Panel */}
          <m.div
            className="fixed bottom-16 max-md:bottom-2 max-h-[90vh] max-md:max-h-[90svh] sm:right-3 right-1 sm:left-auto left-1 z-50 flex"
            initial={{
              opacity: 0,
              y: 400,
              scale: 0.8,
              rotateX: 15,
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              rotateX: 0,
              transition: {
                type: 'spring',
                damping: 25,
                stiffness: 200,
                duration: 0.5,
              },
            }}
            exit={{
              opacity: 0,
              y: 400,
              scale: 0.8,
              rotateX: 15,
              transition: {
                duration: 0.3,
                ease: 'easeInOut',
              },
            }}
          >
            <div className="relative sm:w-[36rem] w-full max-h-[90vh] overflow-hidden bg-surface rounded-2xl bg-gradient-to-br from-white via-neutral-10/50 to-neutral-20">
              <div className="relative flex h-full flex-col">
                <m.div
                  className="relative flex items-center justify-between px-6 py-4 border-b border-hairline/50"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: {
                      delay: 0.1,
                      type: 'spring',
                      damping: 25,
                      stiffness: 300,
                    },
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-full bg-gradient-to-br from-brand-1 to-brand-2 shadow-card">
                      <FeedbackIcon className="size-6 text-white" />
                    </div>
                    <h2 className="text-lg font-semibold text-neutral-100 tracking-tight">
                      Send Feedback
                    </h2>
                  </div>
                  <button
                    onClick={handleClose}
                    className="flex h-8 w-8 max-md:h-10 max-md:w-10 items-center justify-center rounded-full text-neutral-50 transition-all duration-200 hover:bg-neutral-10 hover:text-neutral-80 hover:scale-105 active:scale-95"
                  >
                    <CloseIcon className="h-4 w-4" />
                  </button>
                </m.div>

                {/* Content */}
                {!submissionSuccess ? (
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="flex-1 overflow-y-auto"
                  >
                    <m.div
                      className="p-6 space-y-8"
                      initial="hidden"
                      animate="visible"
                      variants={{
                        hidden: {},
                        visible: {
                          transition: {
                            staggerChildren: 0.1,
                            delayChildren: 0.2,
                          },
                        },
                      }}
                    >
                      <m.div
                        className="space-y-3"
                        variants={{
                          hidden: { opacity: 0, y: 20 },
                          visible: {
                            opacity: 1,
                            y: 0,
                            transition: {
                              type: 'spring',
                              damping: 25,
                              stiffness: 300,
                            },
                          },
                        }}
                      >
                        <label className="block text-sm font-semibold text-neutral-80">
                          Type
                        </label>
                        <div className="">
                          <TabList2
                            tabs={feedbackTabs}
                            selectedTab={feedbackType}
                            onTabClick={handleTabClick}
                            className=""
                          />
                        </div>
                      </m.div>

                      <m.div
                        className="space-y-3"
                        variants={{
                          hidden: { opacity: 0, y: 20 },
                          visible: {
                            opacity: 1,
                            y: 0,
                            transition: {
                              type: 'spring',
                              damping: 25,
                              stiffness: 300,
                            },
                          },
                        }}
                      >
                        <GenericInput
                          {...register('subject')}
                          placeholder={
                            feedbackType === 'feedback'
                              ? 'What would you like to share?'
                              : 'Brief description of the issue'
                          }
                          label="Subject"
                          required
                          autoFocus
                        />
                        {errors.subject && (
                          <p className="mt-2 text-sm text-status-error-700 font-medium">
                            {errors.subject.message}
                          </p>
                        )}
                      </m.div>

                      <m.div
                        className="space-y-3"
                        variants={{
                          hidden: { opacity: 0, y: 20 },
                          visible: {
                            opacity: 1,
                            y: 0,
                            transition: {
                              type: 'spring',
                              damping: 25,
                              stiffness: 300,
                            },
                          },
                        }}
                      >
                        <Textarea
                          {...register('description')}
                          rows={10}
                          placeholder={
                            feedbackType === 'feedback'
                              ? 'Share your thoughts, suggestions, or ideas...'
                              : 'Please describe the bug in detail, including steps to reproduce it'
                          }
                          label="Description"
                          required
                        />
                        {errors.description && (
                          <p className="mt-2 text-sm text-status-error-700 font-medium">
                            {errors.description.message}
                          </p>
                        )}
                      </m.div>
                    </m.div>
                  </form>
                ) : (
                  <div className="flex-1 flex items-center justify-center p-6">
                    <m.div
                      className="text-center space-y-4"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        transition: {
                          type: 'spring',
                          damping: 25,
                          stiffness: 300,
                        },
                      }}
                    >
                      <div className="flex justify-center">
                        <div className="flex size-16 items-center justify-center rounded-full bg-status-good/10">
                          <CheckIcon className="size-8 text-status-good-700" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-neutral-100">
                          Thank you!
                        </h3>
                        <p className="text-neutral-70 leading-relaxed">
                          We truly appreciate your{' '}
                          {submittedType === 'bug' ? 'bug report' : 'feedback'}.
                          {submittedType === 'bug'
                            ? " We'll investigate and fix it as soon as possible."
                            : ' Your input helps us improve our service.'}
                        </p>
                      </div>
                    </m.div>
                  </div>
                )}

                {/* Footer */}
                <m.div
                  className="relative border-t border-hairline bg-gradient-to-r from-neutral-10/50 to-neutral-20/30 px-6 py-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: {
                      delay: 0.5,
                      type: 'spring',
                      damping: 25,
                      stiffness: 300,
                    },
                  }}
                >
                  {!submissionSuccess ? (
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        onClick={handleClose}
                        style="normal"
                      >
                        Cancel
                      </Button>
                      <Button
                        style="primary"
                        type="submit"
                        disabled={isSubmitting}
                        onClick={handleSubmit(onSubmit)}
                      >
                        {isSubmitting ? (
                          <div className="flex items-center justify-center gap-2">
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            Sending...
                          </div>
                        ) : (
                          'Send Feedback'
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex justify-center">
                      <Button
                        type="button"
                        onClick={handleClose}
                        style="primary"
                      >
                        Close
                      </Button>
                    </div>
                  )}
                </m.div>
              </div>
            </div>
          </m.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default FeedbackPanel
